import { TableView } from '@components/button';
import {
  GridView,
  ListView,
  VolumeExplorerBanner,
  VolumeExplorerHeader,
} from '@components/volumeExplorer';
import { InpaintProgressModal } from '@components/volumeExplorer/header/services/inpaint/InpaintProgressModal';
import { UploadProgressModal } from '@components/volumeExplorer/header/uploadFile/UploadProgressModal';
import { useGetVolumeDataQuery, useGetVolumeDetailsVolumeDataQuery } from '@generated/graphql';
import { Container, Stack } from '@mui/material';
import { useMemo, useState } from 'react';

type OrganizationVolumeExplorerProps = {
  volumeId: string;
  onClearVolumeId: () => void;
};

export const OrganizationVolumeExplorer = ({
  volumeId,
  onClearVolumeId,
}: OrganizationVolumeExplorerProps) => {
  const { data: volumeMetadataData } = useGetVolumeDetailsVolumeDataQuery({
    variables: {
      volumeId,
    },
  });
  const volumeMetadata = volumeMetadataData?.getVolumes?.[0] || {
    volumeId,
    name: '-',
    permission: '',
  };

  const [directory, setDirectory] = useState<string[]>([]);
  const dir = directory.length > 0 ? `${directory.join('')}` : '/';

  const [tableView, setTableView] = useState<TableView>('grid');

  const [hasMore, setHasMore] = useState(true);

  const {
    data,
    refetch: refetchVolumeData,
    loading,
    fetchMore,
  } = useGetVolumeDataQuery({
    variables: {
      volumeId,
      dir,
      limit: 50,
    },
  });

  const volumeData = useMemo(
    () =>
      data?.getVolumeData?.keys?.map((item) => ({
        ...item,
        type: item?.hash?.length > 0 ? 'file' : 'folder',
        name: item?.key?.split('/')[0],
      })) || [],
    [data],
  );

  const onFetchMore = async () => {
    if (!hasMore) {
      return;
    }
    if (volumeData?.length < 50) {
      setHasMore(false);
      return;
    }
    const { data: fetchMoreData } = await fetchMore({
      variables: { cursor: volumeData.length.toString() },
    });
    if ((fetchMoreData?.getVolumeData?.keys as []).length < 50) {
      setHasMore(false);
    }
  };

  const props = {
    loading,
    volumeId,
    volumeData,
    volumeMetadata,
    directory,
    setDirectory,
    refetchVolumes: refetchVolumeData,
    dir,
    fetchMore: onFetchMore,
  };

  return (
    <Container
      disableGutters
      sx={{
        height: '100%',
        width: '100%',
        display: 'flex',
        flexDirection: 'column',
        flex: 1,
        gap: 6,
      }}
    >
      <VolumeExplorerBanner
        directory={directory}
        name={volumeMetadata.name}
        permission={volumeMetadata.permission}
        onDirectoryChange={setDirectory}
        onExplorerExit={onClearVolumeId}
      />
      <VolumeExplorerHeader
        directory={directory}
        tableView={tableView}
        volumeId={volumeId}
        permission={volumeMetadata.permission}
        onUploadData={refetchVolumeData}
        onTableViewChange={setTableView}
        onFolderCreate={refetchVolumeData}
      />
      {tableView === 'grid' ? <GridView {...props} /> : <ListView {...props} />}
      <Stack
        sx={{
          position: 'absolute',
          bottom: 0,
          left: 16,
        }}
        gap={2}
      >
        <UploadProgressModal tableView={tableView} />
        <InpaintProgressModal
          tableView={tableView}
          workspaceId=""
          setFolderHistory={setDirectory}
        />
      </Stack>
    </Container>
  );
};
